import { Key } from 'react'
import { APIListDataWithTotal } from '../common/Types/Types'

/**
 * One licence type entry within a CX product licence summary tile.
 */
export type CxLicenceBreakdownItem = {
    Label: string
    Count: number
    /** Effective licence cap for this licence (telsys cap first, else CME cap), when set. */
    Cap?: number
}

/**
 * Aggregated licence data for a single CX product, used to drive a summary tile.
 */
export type CxLicenceSummary = {
    Total: number
    /** Effective total licence cap for the product, when one applies. */
    Cap?: number
    Breakdown: readonly CxLicenceBreakdownItem[]
}

export const LicenceManagementEntryType = {
    DEVICE: 'device',
    USER: 'user'
} as const

export type LicenceManagementEntryType = (typeof LicenceManagementEntryType)[keyof typeof LicenceManagementEntryType]

export type LicenceManagementSummary = {
    TotalDevices: number
    TotalUsers: number
    AssignedDevices: number
    UnassignedDevices: number
    CXALicences?: CxLicenceSummary
    CXSLicences?: CxLicenceSummary
    CXRLicences?: CxLicenceSummary
    CXILicences?: CxLicenceSummary
}

export type LicenceManagementListItem = {
    EntryType?: LicenceManagementEntryType
    Description?: string
    TelSysID?: number
    TelSysDescription?: string
    PartIDInTelSys?: number
    PartitionDescription?: string
    DeviceNumber?: string
    TelSysUsername?: string
    HasDevice?: boolean
    Username?: string
    FullName?: string
    EmailAddr?: string
    AgentNumber?: string
    WelcomeEmail?: boolean
    UserLicence?: string
    CXILicence?: string
    CXSLicence?: string
    CXRLicence?: string
    CXRecordEnabled?: boolean
    /** Device-level CX Score monitoring flag for the row's device. */
    CXScoreEnabled?: boolean
    /** Device-level CX Record monitoring flag for the row's device. */
    CXREnabled?: boolean
    /** Whether an ACD agent exists for the row's device (assigned to the user or not). */
    HasAgent?: boolean
    Status?: string
}

export type LicenceManagementListRow = {
    key: Key
    Description: string
    EntryType: LicenceManagementEntryType
    EntryTypeDisplay: string
    TelSysID: number
    TelSysDescription: string
    PartIDInTelSys: number | string
    PartitionDescription: string
    DeviceNumber: string
    TelSysUsername: string
    HasDevice: boolean
    Username: string
    FullName: string
    EmailAddr: string
    AgentNumber: string
    WelcomeEmail: boolean
    UserLicence: string
    UserLicenceValue: string
    CXILicence: string
    CXILicenceValue: string
    CXSLicence: string
    CXSLicenceValue: string
    CXRLicence: string
    CXRLicenceValue: string
    CXRecordEnabled: boolean
    CXScoreEnabled: boolean
    CXREnabled: boolean
    HasAgent: boolean
    Licences: string
    Actions?: never
    Status: string
}

export type LicenceManagementListResponse = APIListDataWithTotal<LicenceManagementListItem> & {
    Data: APIListDataWithTotal<LicenceManagementListItem>['Data'] & {
        Summary?: LicenceManagementSummary
    }
}
